import { Routes, Route, Link } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CanvasApp } from './components/CanvasApp';

// Query client with default settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function HealthPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">ARM Product Discovery</h1>
            <nav className="flex space-x-4">
              <Link to="/" className="text-gray-600 hover:text-gray-900">
                Home
              </Link>
              <Link to="/discover" className="text-gray-600 hover:text-gray-900">
                Products
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-semibold text-gray-900">System Health</h2>
          <p className="mt-2 text-gray-600">
            Check the health endpoint at <code>/api/v1/health</code> for status.
          </p>
          <div className="mt-4">
            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
              System Operational
            </span>
          </div>
        </div>
      </main>
    </div>
  );
}

function HomePage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">ARM Product Discovery</h1>
            <nav className="flex space-x-6">
              <Link to="/" className="text-gray-900 font-medium hover:text-blue-600">
                Home
              </Link>
              <Link to="/discover" className="text-gray-600 hover:text-blue-600">
                Browse Products
              </Link>
              <Link to="/health" className="text-gray-600 hover:text-gray-900">
                Health
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Hero section */}
        <div className="text-center">
          <h2 className="text-4xl font-bold text-gray-900 sm:text-5xl">
            Find the right product,
            <span className="text-blue-600">right now</span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-gray-600">
            Browse catalogs, explore categories, and discover products with real-time pricing from
            Salesforce ARM.
          </p>
          <div className="mt-10 flex justify-center gap-4">
            <Link
              to="/discover"
              className="rounded-lg bg-blue-600 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Start Browsing
            </Link>
            <Link
              to="/health"
              className="rounded-lg bg-gray-200 px-8 py-4 text-lg font-semibold text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Check Status
            </Link>
          </div>
        </div>

        {/* Status indicators */}
        <div className="mt-16">
          <div className="rounded-lg border border-gray-200 bg-white px-6 py-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="flex h-3 w-3 rounded-full bg-green-500" aria-hidden="true" />
                <span className="text-sm font-medium text-gray-900">M01: Product Discovery</span>
                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                  Implemented
                </span>
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              Browse catalogs → Select category → View products → Search → Select product
            </p>
          </div>

          {/* Milestones */}
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-green-200 bg-green-50 p-6">
              <h3 className="font-medium text-green-900">Product Discovery</h3>
              <p className="mt-1 text-sm text-green-700">Browse catalogs and search products</p>
              <span className="mt-3 inline-flex items-center rounded-full bg-green-200 px-2.5 py-0.5 text-xs font-medium text-green-800">
                Complete
              </span>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <h3 className="font-medium text-gray-900">Configuration</h3>
              <p className="mt-1 text-sm text-gray-500">Configure selected products</p>
              <span className="mt-3 inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                Planned
              </span>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <h3 className="font-medium text-gray-900">Pricing</h3>
              <p className="mt-1 text-sm text-gray-500">View calculated pricing</p>
              <span className="mt-3 inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                Planned
              </span>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <h3 className="font-medium text-gray-900">Quote Builder</h3>
              <p className="mt-1 text-sm text-gray-500">Build and manage quotes</p>
              <span className="mt-3 inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                Planned
              </span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/discover" element={<CanvasApp />} />
        <Route path="/health" element={<HealthPage />} />
      </Routes>
    </QueryClientProvider>
  );
}
